import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Security,
  Storage,
  CloudSync,
  Warning,
  CheckCircle,
  Error,
  Info,
  Refresh,
  Download,
  Build,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { useSnackbar } from 'notistack';

const SystemSettings = () => {
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState({});
  const [reconciliation, setReconciliation] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [settings, setSettings] = useState({
    showLikeCount: false,
    maintenanceMode: false,
    debugMode: false,
  });
  const [reconDialogOpen, setReconDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const { enqueueSnackbar } = useSnackbar();
  const [subPrices, setSubPrices] = useState({ pro: '', premium: '', creditPrice: '' });
  const [subPricesLoading, setSubPricesLoading] = useState(false);
  const [subPricesSaving, setSubPricesSaving] = useState(false);

  // Business rules state
  const [businessRules, setBusinessRules] = useState({
    maxDailySpeechCount: 0,
    maxWordsPerGeneration: 80,
    maxContentLength: 4000,
    proUserImageCredits: 50,
    premiumUserImageCredits: -1, // -1 = unlimited
  });
  const [businessRulesLoading, setBusinessRulesLoading] = useState(false);
  const [businessRulesSaving, setBusinessRulesSaving] = useState(false);

  // Performance settings state
  const [performanceSettings, setPerformanceSettings] = useState({
    cacheExpiryMinutes: 2,
    requestTimeoutSeconds: 20,
    defaultPageSize: 20,
    maxBatchSize: 500,
  });
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceSaving, setPerformanceSaving] = useState(false);

  const fetchSystemData = useCallback(async () => {
    try {
      setLoading(true);
      const [auditRes, alertsRes, duplicatesRes, configRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/payment-audit`),
        axios.get(`${API_BASE_URL}/api/payment-security-alerts?reviewed=false`),
        axios.get(`${API_BASE_URL}/api/duplicate-users`),
        axios.get(`${API_BASE_URL}/api/config/like-count`)
      ]);

      setSystemStatus({
        totalRevenue: auditRes.data.summary.totalRevenue || 0,
        totalTransactions: auditRes.data.summary.totalTransactions || 0,
        failedTransactions: auditRes.data.summary.failedTransactions || 0,
        unrevAlerts: alertsRes.data.length || 0,
        duplicateUsers: duplicatesRes.data.duplicateEmailCount || 0,
        lastUpdate: new Date().toISOString(),
      });

      setSettings(prev => ({
        ...prev,
        showLikeCount: configRes.data.showLikeCount || false,
      }));
    } catch (error) {
      console.error('System data fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscriptionPrices = useCallback(async () => {
    setSubPricesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/config/subscription-prices`);
      setSubPrices({
        pro: res.data.pro,
        premium: res.data.premium,
        creditPrice: res.data.creditPrice
      });
    } catch (e) {
      setSubPrices({ pro: '', premium: '', creditPrice: '' });
    } finally {
      setSubPricesLoading(false);
    }
  }, []);

  const fetchBusinessRules = useCallback(async () => {
    setBusinessRulesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/config/business-rules`);
      setBusinessRules(res.data);
    } catch (e) {
      console.log('Business rules not found, using defaults');
    } finally {
      setBusinessRulesLoading(false);
    }
  }, []);

  const fetchPerformanceSettings = useCallback(async () => {
    setPerformanceLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/config/performance`);
      setPerformanceSettings(res.data);
    } catch (e) {
      console.log('Performance settings not found, using defaults');
    } finally {
      setPerformanceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemData();
    fetchSubscriptionPrices();
    fetchBusinessRules();
    fetchPerformanceSettings();
  }, [
    fetchSystemData,
    fetchSubscriptionPrices,
    fetchBusinessRules,
    fetchPerformanceSettings,
  ]);

  const handleReconciliation = async () => {
    try {
      setReconLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/payment-reconciliation?date=${selectedDate}`
      );
      setReconciliation(response.data);
      setReconDialogOpen(true);
    } catch (error) {
      console.error('Reconciliation error:', error);
    } finally {
      setReconLoading(false);
    }
  };

  const handleToggleSetting = async (setting) => {
    try {
      if (setting === 'showLikeCount') {
        await axios.post(
          `${API_BASE_URL}/api/config/like-count`,
          { showLikeCount: !settings.showLikeCount }
        );
        setSettings(prev => ({ ...prev, showLikeCount: !prev.showLikeCount }));

        enqueueSnackbar(
          `✅ Like count display ${!settings.showLikeCount ? 'enabled' : 'disabled'} successfully`,
          { variant: 'success', autoHideDuration: 3000 }
        );
      }
      // Add other settings handlers here
    } catch (error) {
      console.error('Setting toggle error:', error);
      enqueueSnackbar(
        `❌ Failed to update setting: ${error.response?.data?.error || error.message}`,
        { variant: 'error', autoHideDuration: 5000 }
      );
    }
  };

  const getSystemHealth = () => {
    const issues = [];
    if (systemStatus.failedTransactions > 10) issues.push('High failed transaction rate');
    if (systemStatus.unrevAlerts > 5) issues.push('Multiple unreviewed security alerts');
    if (systemStatus.duplicateUsers > 0) issues.push('Duplicate users detected');

    if (issues.length === 0) return { status: 'healthy', message: 'All systems operational' };
    if (issues.length <= 2) return { status: 'warning', message: `${issues.length} issues detected` };
    return { status: 'error', message: `${issues.length} critical issues` };
  };

  const systemHealth = getSystemHealth();

  const handleSubPriceChange = (e) => {
    setSubPrices({ ...subPrices, [e.target.name]: e.target.value });
  };

  const handleSaveSubPrices = async () => {
    setSubPricesSaving(true);
    try {
      const payload = {
        pro: Number(subPrices.pro),
        premium: Number(subPrices.premium),
        creditPrice: Number(subPrices.creditPrice)
      };

      console.log('Sending pricing update:', payload);

      const response = await axios.post(
        `${API_BASE_URL}/api/config/subscription-prices`,
        payload
      );

      console.log('Pricing update response:', response.data);
      enqueueSnackbar(
        `✅ ${response.data.message || 'Pricing updated successfully'} - Changes are now live!`,
        {
          variant: 'success',
          persist: false,
          autoHideDuration: 5000
        }
      );

      // Refresh the prices to show updated values
      await fetchSubscriptionPrices();
    } catch (e) {
      console.error('Pricing update error:', e.response?.data || e.message);
      const errorMessage = e.response?.data?.error || 'Failed to update prices';
      enqueueSnackbar(
        `❌ Error: ${errorMessage}`,
        {
          variant: 'error',
          persist: false,
          autoHideDuration: 7000
        }
      );
    } finally {
      setSubPricesSaving(false);
    }
  };

  const handleBusinessRuleChange = (field, value) => {
    setBusinessRules(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBusinessRules = async () => {
    setBusinessRulesSaving(true);
    try {
      const editableBusinessRules = {
        maxWordsPerGeneration: businessRules.maxWordsPerGeneration,
        maxContentLength: businessRules.maxContentLength,
        proUserImageCredits: businessRules.proUserImageCredits,
        premiumUserImageCredits: businessRules.premiumUserImageCredits,
      };
      await axios.post(
        `${API_BASE_URL}/api/config/business-rules`,
        editableBusinessRules
      );

      enqueueSnackbar(
        `✅ Business rules updated successfully!`,
        { variant: 'success', autoHideDuration: 5000 }
      );

      await fetchBusinessRules();
    } catch (e) {
      console.error('Business rules update error:', e.response?.data || e.message);
      enqueueSnackbar(
        `❌ Failed to update business rules: ${e.response?.data?.error || e.message}`,
        { variant: 'error', autoHideDuration: 7000 }
      );
    } finally {
      setBusinessRulesSaving(false);
    }
  };



  const handlePerformanceChange = (field, value) => {
    setPerformanceSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePerformance = async () => {
    setPerformanceSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/config/performance`,
        performanceSettings
      );

      enqueueSnackbar(
        `✅ Performance settings updated successfully!`,
        { variant: 'success', autoHideDuration: 5000 }
      );

      await fetchPerformanceSettings();
    } catch (e) {
      console.error('Performance settings update error:', e.response?.data || e.message);
      enqueueSnackbar(
        `❌ Failed to update performance settings: ${e.response?.data?.error || e.message}`,
        { variant: 'error', autoHideDuration: 7000 }
      );
    } finally {
      setPerformanceSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>

      {/* System Health Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Alert
            severity={systemHealth.status}
            icon={
              systemHealth.status === 'healthy' ? <CheckCircle /> :
              systemHealth.status === 'warning' ? <Warning /> : <Error />
            }
          >
            <Typography variant="h6">{systemHealth.message}</Typography>
            <Typography variant="body2">
              Last updated: {new Date(systemStatus.lastUpdate).toLocaleString()}
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" color="primary">
                    ₦{systemStatus.totalRevenue?.toLocaleString()}
                  </Typography>
                </Box>
                <Storage color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Transactions
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {systemStatus.totalTransactions}
                  </Typography>
                  <Typography variant="body2" color={systemStatus.failedTransactions > 0 ? 'error' : 'textSecondary'}>
                    {systemStatus.failedTransactions} failed
                  </Typography>
                </Box>
                <CloudSync color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Security Alerts
                  </Typography>
                  <Typography variant="h5" color={systemStatus.unrevAlerts > 0 ? 'error' : 'primary'}>
                    {systemStatus.unrevAlerts}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Unreviewed
                  </Typography>
                </Box>
                <Security color={systemStatus.unrevAlerts > 0 ? 'error' : 'primary'} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Duplicate Users
                  </Typography>
                  <Typography variant="h5" color={systemStatus.duplicateUsers > 0 ? 'warning' : 'primary'}>
                    {systemStatus.duplicateUsers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Need attention
                  </Typography>
                </Box>
                <Warning color={systemStatus.duplicateUsers > 0 ? 'warning' : 'primary'} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* System Configuration */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
              System Configuration
            </Typography>

            <List>
              <ListItem>
                <ListItemText primary="Show Like Count in Flutter UI" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showLikeCount}
                      onChange={() => handleToggleSetting('showLikeCount')}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemText
                  primary="Maintenance Mode"
                  secondary="Temporarily disable user access for maintenance"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceMode}
                      onChange={() => handleToggleSetting('maintenanceMode')}
                      disabled
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemText
                  primary="Debug Mode"
                  secondary="Enable detailed logging for troubleshooting"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.debugMode}
                      onChange={() => handleToggleSetting('debugMode')}
                      disabled
                    />
                  }
                  label=""
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* System Tools */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Build sx={{ mr: 1, verticalAlign: 'middle' }} />
              System Tools
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchSystemData}
                fullWidth
              >
                Refresh System Status
              </Button>

              <Box>
                <TextField
                  type="date"
                  label="Reconciliation Date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  fullWidth
                  sx={{ mb: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleReconciliation}
                  disabled={reconLoading}
                  fullWidth
                >
                  {reconLoading ? 'Generating...' : 'Generate Payment Reconciliation'}
                </Button>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  More administrative tools will be available in future updates.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>

        {/* System Information */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              System Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Backend API
                </Typography>
                <Chip label="Online" color="success" size="small" />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Database
                </Typography>
                <Chip label="Connected" color="success" size="small" />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Payment Gateway
                </Typography>
                <Chip label="Operational" color="success" size="small" />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  S3 Storage
                </Typography>
                <Chip label="Available" color="success" size="small" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Pricing Configuration */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pricing Configuration
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Credit Price (NGN per credit)"
                name="creditPrice"
                type="number"
                value={subPrices.creditPrice}
                onChange={handleSubPriceChange}
                disabled={subPricesLoading || subPricesSaving}
                helperText="Price that free users pay per image credit"
              />
              <TextField
                label="Pro Plan Price (NGN/month)"
                name="pro"
                type="number"
                value={subPrices.pro}
                onChange={handleSubPriceChange}
                disabled={subPricesLoading || subPricesSaving}
                helperText={`Monthly subscription price for Pro plan (${businessRules.proUserImageCredits} images/month)`}
              />
              <TextField
                label="Premium Plan Price (NGN/month)"
                name="premium"
                type="number"
                value={subPrices.premium}
                onChange={handleSubPriceChange}
                disabled={subPricesLoading || subPricesSaving}
                helperText="Monthly subscription price for Premium plan (unlimited images)"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSubPrices}
                disabled={subPricesLoading || subPricesSaving}
                startIcon={subPricesSaving ? <CircularProgress size={20} /> : null}
              >
                {subPricesSaving ? 'Saving...' : 'Save Pricing'}
              </Button>

              {subPricesLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="textSecondary">
                    Loading current prices...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Business Rules Configuration */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Business Rules Configuration
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Default Daily Speech Limit"
                type="number"
                value={businessRules.maxDailySpeechCount}
                disabled={businessRulesLoading || businessRulesSaving}
                InputProps={{ readOnly: true }}
                helperText="Managed by DEFAULT_DAILY_SPEECH_LIMIT"
              />
              <TextField
                label="Max Words Per Generation"
                type="number"
                value={businessRules.maxWordsPerGeneration}
                onChange={(e) => handleBusinessRuleChange('maxWordsPerGeneration', parseInt(e.target.value))}
                disabled={businessRulesLoading || businessRulesSaving}
                helperText="Maximum words allowed in each speech generation request"
              />
              <TextField
                label="Max Content Length (Characters)"
                type="number"
                value={businessRules.maxContentLength}
                onChange={(e) => handleBusinessRuleChange('maxContentLength', parseInt(e.target.value))}
                disabled={businessRulesLoading || businessRulesSaving}
                helperText="Maximum character length for AI-generated content responses"
              />
              <TextField
                label="Pro User Image Credits per Month"
                type="number"
                value={businessRules.proUserImageCredits}
                onChange={(e) => handleBusinessRuleChange('proUserImageCredits', parseInt(e.target.value))}
                disabled={businessRulesLoading || businessRulesSaving}
                helperText="Monthly image generation credits for Pro users (enter -1 for unlimited)"
              />
              <TextField
                label="Premium User Image Credits per Month"
                type="number"
                value={businessRules.premiumUserImageCredits}
                onChange={(e) => handleBusinessRuleChange('premiumUserImageCredits', parseInt(e.target.value))}
                disabled={businessRulesLoading || businessRulesSaving}
                helperText="Monthly image generation credits for Premium users (-1 = unlimited, recommended)"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveBusinessRules}
                disabled={businessRulesLoading || businessRulesSaving}
                startIcon={businessRulesSaving ? <CircularProgress size={20} /> : null}
              >
                {businessRulesSaving ? 'Saving...' : 'Save Business Rules'}
              </Button>

              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Contract-Honor System:</strong> Changes only affect new subscribers. Existing subscribers keep their original purchased terms until renewal.
                </Typography>
              </Alert>

              {businessRulesLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="textSecondary">
                    Loading current business rules...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Performance Settings Configuration */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Performance Settings
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Cache Expiry (Minutes)"
                type="number"
                value={performanceSettings.cacheExpiryMinutes}
                onChange={(e) => handlePerformanceChange('cacheExpiryMinutes', parseInt(e.target.value))}
                disabled={performanceLoading || performanceSaving}
                helperText="How long to cache pricing and configuration data"
              />
              <TextField
                label="Request Timeout (Seconds)"
                type="number"
                value={performanceSettings.requestTimeoutSeconds}
                onChange={(e) => handlePerformanceChange('requestTimeoutSeconds', parseInt(e.target.value))}
                disabled={performanceLoading || performanceSaving}
                helperText="Maximum time to wait for payment API responses"
              />
              <TextField
                label="Default Page Size"
                type="number"
                value={performanceSettings.defaultPageSize}
                onChange={(e) => handlePerformanceChange('defaultPageSize', parseInt(e.target.value))}
                disabled={performanceLoading || performanceSaving}
                helperText="Default number of items per page in admin listings"
              />
              <TextField
                label="Max Batch Size"
                type="number"
                value={performanceSettings.maxBatchSize}
                onChange={(e) => handlePerformanceChange('maxBatchSize', parseInt(e.target.value))}
                disabled={performanceLoading || performanceSaving}
                helperText="Maximum number of items to process in a single batch operation"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePerformance}
                disabled={performanceLoading || performanceSaving}
                startIcon={performanceSaving ? <CircularProgress size={20} /> : null}
              >
                {performanceSaving ? 'Saving...' : 'Save Performance Settings'}
              </Button>

              {performanceLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="textSecondary">
                    Loading current performance settings...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Reconciliation Dialog */}
      <Dialog
        open={reconDialogOpen}
        onClose={() => setReconDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Payment Reconciliation Report - {selectedDate}
        </DialogTitle>
        <DialogContent>
          {reconciliation && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>Summary</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Total Transactions"
                        secondary={reconciliation.totalTransactions}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Successful Payments"
                        secondary={reconciliation.successfulPayments}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Failed Payments"
                        secondary={reconciliation.failedPayments}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Revenue"
                        secondary={`₦${reconciliation.totalRevenue?.toLocaleString()}`}
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>Breakdown</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Credits Sold"
                        secondary={reconciliation.creditsSold}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Subscriptions Sold"
                        secondary={reconciliation.subscriptionsSold}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Webhook Processed"
                        secondary={reconciliation.webhookVsDirectApi?.webhook || 0}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Direct API Processed"
                        secondary={reconciliation.webhookVsDirectApi?.directApi || 0}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {reconciliation.securityAlerts > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Security Alerts</Typography>
                  <Typography variant="body2">
                    {reconciliation.securityAlerts} security alerts were generated on this date.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings;
